import React, { useState, useEffect } from 'react';
import MakeRequest from '../Helpers/MakeRequest';
import { Container, Row, Col, Table, Button, Form } from 'react-bootstrap';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import apiUrl from '../Helpers/APIWrapper';
import toBlob from '../Helpers/FileBlobs';
import EditProductRows from '../Products/EditProductRows';
import EditProductTypes from '../Products/EditProductTypes';
import { EditProductsProvider, useEditProducts } from '../Context/EditProductContext';

const AddProductButton = () => {
    const { addProduct, setSaveDisabled } = useEditProducts();
    const handleAdd = () => {
        setSaveDisabled(false);
        addProduct({
            name: '',
            type: null,
            category: '',
            contentSwe: '',
            contentEng: '',
            image: '',
            safetySheets: [],
            imageSrc: {},
            prioritized: false
        });
    };
    return (
        <Button size="sm" style={{ marginLeft: 10 }} onClick={handleAdd} title="Add new row">
            <i className="fa-solid fa-plus"></i>
        </Button>
    );
};

const SaveControls = ({ l }) => {
    const { state, setSaveDisabled, saveDisabled, addProduct } = useEditProducts();
    const { products, productTypes } = state;

    const handleSaveChanges = async () => {

        // Validate products
        for (const product of products) {
            if (
                !product.name ||
                !product.type ||
                !product.type.typeSwe ||
                !product.type.typeEng ||
                !product.category ||
                !product.contentSwe ||
                !product.contentEng
            ) {
                alert(l('Alla fält måste fyllas i för varje produkt.', 'All fields must be filled in for each product.'));
                return;
            }
        }

        // Validate product types and subcategories (object format)
        for (const pt of productTypes) {
            if (
                !pt.typeSwe ||
                !pt.typeEng ||
                pt.typeSwe.trim() === "" ||
                pt.typeEng.trim() === ""
            ) {
                alert(l("Alla produkttyper måste ha ett namn på båda språken.", "All product types must have a name in both languages."));
                return;
            }
        }

        try {

            const formData = new FormData();

            for (const [index, product] of products.entries()) {

                if (typeof product.imageSrc?.file === 'string' && product.imageSrc?.file.startsWith('data:')) {
                    const blob = toBlob(product.imageSrc.file ?? product.imageSrc);
                    const name = product.imageSrc?.name ?? `image-${index}`;
                    formData.append('files', blob, name);
                    formData.append(`imageName-${index}`, name);
                }

                if (Array.isArray(product.safetySheets)) {
                    for (const [fileIndex, file] of product.safetySheets.entries()) {
                        if (file.file && file.file.startsWith("data:")) {
                            const blob = toBlob(file.file ?? file);
                            const name = file?.name ?? `safetySheets-${index}-${fileIndex}`;
                            formData.append('files', blob, name);
                            formData.append(`safetySheetName-${index}-${fileIndex}`, name);
                        } else {
                            formData.append(`safetySheetName-${index}-${fileIndex}`, file.name || '');
                            formData.append(`safetySheetPath-${index}-${fileIndex}`, file.file || '');
                        }

                        formData.append(`safetySheetTitleSv-${index}-${fileIndex}`, file.titleSv || '');
                        formData.append(`safetySheetTitleEn-${index}-${fileIndex}`, file.titleEn || '');
                        formData.append(`safetySheetLanguage-${index}-${fileIndex}`, file.language || '');
                        formData.append(`safetySheetType-${index}-${fileIndex}`, file.type || '');
                    }
                }

                formData.append(`id-${index}`, product._id);
                formData.append(`name-${index}`, product.name);
                formData.append(`type-${index}`, JSON.stringify(product.type));
                formData.append(`category-${index}`, JSON.stringify(product.category));
                formData.append(`contentSwe-${index}`, product.contentSwe);
                formData.append(`contentEng-${index}`, product.contentEng);
                formData.append(`prioritized-${index}`, product.prioritized);
            }

            formData.append('productTypes', JSON.stringify(productTypes));

            const response = await axios.post(`${apiUrl}/content/products`, formData, {
                withCredentials: true,
            });
            if (response.status === 200) {
                alert(l("Ändringar sparade!", "Changes saved!"));
                setSaveDisabled(true);
                window.location.reload()
            } else {
                alert(l("Misslyckades med att spara ändringar.", "Failed to save changes."));
            }

        } catch (error) {
            console.error("Validation error:", error);
            alert(l("Ett fel uppstod vid validering av data.", "An error occurred while validating data."));
            return;
        }
    };

    return (
        <Row className="d-flex justify-content-center">
            <Button style={{ width: 200 }} onClick={handleSaveChanges} disabled={saveDisabled}>
                {l("Spara ändringar", "Save Changes")}
            </Button>
        </Row>
    );
};

const EditProducts = () => {
    const { l } = useOutletContext();

    return (
        <>
            <EditProductsProvider>
                <Row>
                    <Col xxl={4} xl={4} lg={5}>
                        <Form.Label column style={{ display: "flex", alignItems: "center", fontWeight: "bold" }}>
                            {l("Lägg till produkt", "Add product")}
                            <AddProductButton />
                        </Form.Label>
                    </Col>
                    <EditProductRows />
                </Row>
                <Row>
                    <EditProductTypes />
                </Row>
                <SaveControls l={l} />
            </EditProductsProvider>
        </>
    );
};

export default EditProducts;
